import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { snakeCase } from 'lodash';

import { dateToIsoDateString } from '../dateUtils';
import { firebaseAnalytics } from '../firebaseUtils';
import { omitUndefined } from './analytics';
import { BillingCycleType, ConversionStageType, UserProperties } from './events';

// ###################################################################################
// # TRACKING EVENTS FUNCTIONS                                                       #
// ###################################################################################

/**
 * Standardized structure for Google Analytics event tracking.
 *
 * - `name`: Event type identifier (required).
 * - `category`: Logical grouping of the event (optional).
 * - `label`: Descriptive label for the event (optional).
 * - `value`: Numeric only (e.g. cost, score, count).
 * - `properties`: Free-form key/value pairs for additional details.
 * - `isRawEvent`: If true, disables automatic snake_case formatting
 *   for `category` and `label`.
 */
type GAProps = {
  name: string;
  category?: string;
  label?: string;
  value?: number;
  properties?: Record<string, string | number | boolean | null>;
  isRawEvent?: boolean;
};

/**
 * Track a custom event in Google Analytics 4 with consistent structure.
 *
 * - Converts `name`, `category`, and `label` into `snake_case` by default.
 * - Keeps `value` numeric (GA4 requirement).
 * - Supports free-form event parameters via `properties`.
 *
 * @param args - Object containing event details.
 *
 * @example
 * googleAnalyticsTrackEvent({
 *   name: "click_campaign_delete",
 *   category: "campaigns_list/campaign",
 *   label: "delete_button",
 *   value: 1,
 *   properties: { item_name: "Summer Sale" }
 * });
 */
export function googleAnalyticsTrackEvent({
  name,
  category,
  label,
  value,
  properties = {},
  isRawEvent = false,
}: GAProps) {
  const eventName = snakeCase(name.toLowerCase());
  const params = {
    ...(omitUndefined({
      category: category
        ? isRawEvent
          ? category
          : snakeCase(category.toLowerCase())
        : undefined,
      label: label ? (isRawEvent ? label : snakeCase(label.toLowerCase())) : undefined,
      value,
    }) as Record<string, unknown>),
    ...properties,
  };

  logEvent(firebaseAnalytics, eventName, params);
}

// ###################################################################################
// # USER PROPERTIES FUNCTIONS                                                       #
// ###################################################################################

/**
 * Sets the user ID in GA and also writes it as a user property.
 *
 * - `setUserId` links the user ID for event attribution.
 * - `setUserProperties` persists the ID as `user_id` for segmentation.
 *
 * @param userId - The user's unique identifier.
 */
export function setGAUserPropertyUserId(userId: string) {
  setUserId(firebaseAnalytics, userId);
  setUserProperties(firebaseAnalytics, { [UserProperties.USER_ID]: userId });
}

/**
 * Sets the 'signup_date' user property to the current date.
 *
 * - Uses ISO string format "YYYY-MM-DD".
 */
export function setGAUserPropertySignedUpDate() {
  setUserProperties(firebaseAnalytics, {
    [UserProperties.SIGNUP_DATE]: dateToIsoDateString(new Date()),
  });
}

/**
 * Sets the 'conversion_stage' user property.
 *
 * @param conversionStage - The user's current plan or conversion stage.
 * @example "free_trial" | "basic" | "pro"
 */
export function setGAUserPropertyConversionStage(conversionStage: ConversionStageType) {
  setUserProperties(firebaseAnalytics, {
    [UserProperties.CONVERSION_STAGE]: conversionStage,
  });
}

/**
 * Sets the 'signup_source' user property.
 *
 * @param source - The source of the user's signup (e.g., ad campaign, referral, organic).
 */
export function setGAUserPropertySignupSource(source: string) {
  setUserProperties(firebaseAnalytics, { [UserProperties.SIGNUP_SOURCE]: source });
}

/**
 * Sets the 'last_active_date' user property to the current date.
 *
 * - Uses ISO string format "YYYY-MM-DD".
 */
export function setGAUserPropertyLastActiveDate() {
  setUserProperties(firebaseAnalytics, {
    [UserProperties.LAST_ACTIVE_DATE]: dateToIsoDateString(new Date()),
  });
}

/**
 * Sets the 'billing_cycle' user property.
 *
 * @param cycle - The user's billing cycle (e.g., "monthly", "yearly", "lifetime").
 */
export function setGAUserPropertyBillingCycle(cycle: BillingCycleType) {
  setUserProperties(firebaseAnalytics, { [UserProperties.BILLING_CYCLE]: cycle });
}
